import { GetNetworksPaginatedModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/networks/models/inputs/get-networks-paginated.model';
import { GatewayNetworkModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/networks/models/outputs/gateway-network.model';
import { GetUsersPaginatedModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/users/models/inputs/get-users-paginated.model';
import { useUrlSyncedFilters } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/hooks';
import { CreateNetworkModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/customers-service-lib/src/wrapper/inputs/networks/create-network.model';
import { UpdateNetworkModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/customers-service-lib/src/wrapper/inputs/networks/update-network.model';
import {
  Dispatch,
  SetStateAction,
  createContext,
  useContext,
  useMemo,
  useState,
} from 'react';
import { DeepPartialSkipArrayKey } from 'react-hook-form';

import {
  TABLE_LIMITS_OPTIONS,
  TABLE_LIMIT_OPTION_MIN,
} from '@app/config/app.config';
import {
  NETWORK_FILTERS_LSK,
  NETWORK_LIST_OPTIONS_LSK,
} from '@app/config/localstorage-keys.const';
import {
  USER_LIST_SORT_FIELD,
  USER_LIST_SORT_ORDER,
} from '@app/feature/user/user-list/user-list.config';
import { useLocalStorageOptions } from '@shared/hooks/useLocalStorageOptions';

import {
  NETWORK_LIST_DEFAULT_OPTIONS,
  NETWORK_LIST_FILTER_NAME_LIST,
  NETWORK_LIST_SORT_FIELD,
  NETWORK_LIST_SORT_ORDER,
  NetworkListOptionsModel,
} from './network-list/network-list.config';

// /////////////////////////////////////////////////////////////////////////////

export const NetworkContext = createContext<GatewayNetworkModel | null>(null);

export const useNetworkContext = (): GatewayNetworkModel => {
  const context = useContext(NetworkContext);

  if (!context) {
    throw new Error(
      'useNetworkContext must be used within NetworkContext.Provider',
    );
  }

  return context;
};

interface NetworkContextProviderProps {
  data: GatewayNetworkModel;
  children: React.ReactNode;
}

export const NetworkContextProvider = ({
  data,
  children,
}: NetworkContextProviderProps): React.JSX.Element => {
  return (
    <NetworkContext.Provider value={data}>{children}</NetworkContext.Provider>
  );
};

// /////////////////////////////////////////////////////////////////////////////

export interface NetworkListContextProps {
  getPaginatedQueryParameters: GetNetworksPaginatedModel;
  setGetPaginatedQueryParameters: Dispatch<
    SetStateAction<GetNetworksPaginatedModel>
  >;
  getPaginatedLimitList: number[];
  columnOptions: NetworkListOptionsModel;
  setColumnOptions: Dispatch<SetStateAction<NetworkListOptionsModel>>;
  // setGetPaginatedLimitList: Dispatch<SetStateAction<number>>;
}

export const NetworkListContext = createContext<NetworkListContextProps | null>(
  null,
);

export const useNetworkListContext = (): NetworkListContextProps => {
  const context = useContext(NetworkListContext);

  if (!context) {
    throw new Error(
      'useNetworkListContext must be used within NetworkListContext.Provider',
    );
  }

  return context;
};

interface NetworkListContextProviderProps {
  children: React.ReactNode;
}

export const NetworkListContextProvider = ({
  children,
}: NetworkListContextProviderProps): React.JSX.Element => {
  const [getPaginatedQueryParameters, setGetPaginatedQueryParameters] =
    useUrlSyncedFilters<GetNetworksPaginatedModel>(
      {
        // Pagination
        limit: TABLE_LIMITS_OPTIONS.at(-1) ?? TABLE_LIMIT_OPTION_MIN,
        offset: 0,
        // Filterable fields
        customerId: [],
        textToSearch: '',
        // Sortable fields
        sortField: NETWORK_LIST_SORT_FIELD,
        sortOrder: NETWORK_LIST_SORT_ORDER,
      },
      [
        'limit',
        'offset',
        'sortField',
        'sortOrder',
        'textToSearch',
        ...NETWORK_LIST_FILTER_NAME_LIST,
      ],
      NETWORK_FILTERS_LSK,
    );

  const [columnOptions, setColumnOptions] =
    useLocalStorageOptions<NetworkListOptionsModel>(
      NETWORK_LIST_DEFAULT_OPTIONS,
      NETWORK_LIST_OPTIONS_LSK,
    );

  const data = useMemo(() => {
    return {
      getPaginatedQueryParameters,
      setGetPaginatedQueryParameters,
      getPaginatedLimitList: TABLE_LIMITS_OPTIONS,
      columnOptions,
      setColumnOptions,
      // setGetPaginatedLimitList,
    };
  }, [
    getPaginatedQueryParameters,
    setGetPaginatedQueryParameters,
    columnOptions,
    setColumnOptions,
    // setGetPaginatedLimitList,
  ]);

  return (
    <NetworkListContext.Provider value={data}>
      {children}
    </NetworkListContext.Provider>
  );
};

// /////////////////////////////////////////////////////////////////////////////

export const FilterNetworkListContext =
  createContext<DeepPartialSkipArrayKey<GetNetworksPaginatedModel> | null>(
    null,
  );

export const useFilterNetworkListContext =
  (): DeepPartialSkipArrayKey<GetNetworksPaginatedModel> => {
    const context = useContext(FilterNetworkListContext);

    if (!context) {
      throw new Error(
        'useFilterNetworkListContext must be used within FilterNetworkListContext.Provider',
      );
    }

    return context;
  };

interface FilterNetworkListContextProviderProps {
  data: DeepPartialSkipArrayKey<GetNetworksPaginatedModel>;
  children: React.ReactNode;
}

export const FilterNetworkListContextProvider = ({
  data,
  children,
}: FilterNetworkListContextProviderProps): React.JSX.Element => {
  return (
    <FilterNetworkListContext.Provider value={data}>
      {children}
    </FilterNetworkListContext.Provider>
  );
};

// /////////////////////////////////////////////////////////////////////////////

export const FilterSearchNetworkListContext =
  createContext<DeepPartialSkipArrayKey<GetNetworksPaginatedModel> | null>(
    null,
  );

export const useFilterSearchNetworkListContext =
  (): DeepPartialSkipArrayKey<GetNetworksPaginatedModel> => {
    const context = useContext(FilterSearchNetworkListContext);

    if (!context) {
      throw new Error(
        'useFilterSearchNetworkListContext must be used within FilterSearchNetworkListContext.Provider',
      );
    }

    return context;
  };

interface FilterSearchNetworkListContextProviderProps {
  data: DeepPartialSkipArrayKey<GetNetworksPaginatedModel>;
  children: React.ReactNode;
}

export const FilterSearchNetworkListContextProvider = ({
  data,
  children,
}: FilterSearchNetworkListContextProviderProps): React.JSX.Element => {
  return (
    <FilterSearchNetworkListContext.Provider value={data}>
      {children}
    </FilterSearchNetworkListContext.Provider>
  );
};

// /////////////////////////////////////////////////////////////////////////////

export const CreateNetworkContext =
  createContext<DeepPartialSkipArrayKey<CreateNetworkModel> | null>(null);

export const useCreateNetworkContext =
  (): DeepPartialSkipArrayKey<CreateNetworkModel> => {
    const context = useContext(CreateNetworkContext);

    if (!context) {
      throw new Error(
        'useCreateNetworkContext must be used within CreateNetworkContext.Provider',
      );
    }

    return context;
  };

interface CreateNetworkContextProviderProps {
  data: DeepPartialSkipArrayKey<CreateNetworkModel>;
  children: React.ReactNode;
}

export const CreateNetworkContextProvider = ({
  data,
  children,
}: CreateNetworkContextProviderProps): React.JSX.Element => {
  return (
    <CreateNetworkContext.Provider value={data}>
      {children}
    </CreateNetworkContext.Provider>
  );
};

// /////////////////////////////////////////////////////////////////////////////

export const UpdateNetworkContext = createContext<Pick<
  UpdateNetworkModel,
  'network' | 'networkId'
> | null>(null);

export const useUpdateNetworkContext = (): Pick<
  UpdateNetworkModel,
  'network' | 'networkId'
> => {
  const context = useContext(UpdateNetworkContext);

  if (!context) {
    throw new Error(
      'useUpdateNetworkContext must be used within UpdateNetworkContext.Provider',
    );
  }

  return context;
};

interface UpdateNetworkContextProviderProps {
  data: Pick<UpdateNetworkModel, 'network' | 'networkId'>;
  children: React.ReactNode;
}

export const UpdateNetworkContextProvider = ({
  data,
  children,
}: UpdateNetworkContextProviderProps): React.JSX.Element => {
  return (
    <UpdateNetworkContext.Provider value={data}>
      {children}
    </UpdateNetworkContext.Provider>
  );
};

// /////////////////////////////////////////////////////////////////////////////

export interface NetworkUserListContextProps {
  getPaginatedQueryParameters: GetUsersPaginatedModel;
  setGetPaginatedQueryParameters: Dispatch<
    SetStateAction<GetUsersPaginatedModel>
  >;
  getPaginatedLimitList: number[];
  // setGetPaginatedLimitList: Dispatch<SetStateAction<number>>;
}

export const NetworkUserListContext =
  createContext<NetworkUserListContextProps | null>(null);

export const useNetworkUserListContext = (): NetworkUserListContextProps => {
  const context = useContext(NetworkUserListContext);

  if (!context) {
    throw new Error(
      'useNetworkUserListContext must be used within NetworkUserListContext.Provider',
    );
  }

  return context;
};

interface NetworkUserListContextProviderProps {
  data: { network: GatewayNetworkModel };
  children: React.ReactNode;
}

export const NetworkUserListContextProvider = ({
  data,
  children,
}: NetworkUserListContextProviderProps): React.JSX.Element => {
  const [getPaginatedQueryParameters, setGetPaginatedQueryParameters] =
    useState<GetUsersPaginatedModel>({
      // Pagination
      limit: TABLE_LIMITS_OPTIONS.at(-1) ?? TABLE_LIMIT_OPTION_MIN,
      offset: 0,
      // Filterable fields
      networkId: [data.network.id],
      // Sortable fields
      sortField: USER_LIST_SORT_FIELD,
      sortOrder: USER_LIST_SORT_ORDER,
    });

  const value = useMemo(() => {
    return {
      getPaginatedQueryParameters,
      setGetPaginatedQueryParameters,
      getPaginatedLimitList: TABLE_LIMITS_OPTIONS,
      // setGetPaginatedLimitList,
    };
  }, [
    getPaginatedQueryParameters,
    setGetPaginatedQueryParameters,
    // setGetPaginatedLimitList,
  ]);

  return (
    <NetworkUserListContext.Provider value={value}>
      {children}
    </NetworkUserListContext.Provider>
  );
};
